import React, { useState, useEffect } from 'react';
import { X, Briefcase, AlertCircle, Calendar, MapPin, Sparkles, Zap, ListChecks, Layers, GraduationCap, Clock, CheckCircle2, MoreHorizontal, FileText, Upload, ChevronRight, Info, Link, Building, Hash, Settings2, MessageSquare, User, Plus, Trash2, GripVertical, HelpCircle, AlignLeft, Radio, CheckSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../styles/pages/User.scss';

const AddOpportunityModal = ({ isOpen, onClose, onAdd, editData, token }) => {
    const initialState = {
        title: '',
        org: '',
        type: 'Internship',
        excerpt: '',
        fullDescription: '',
        badges: '',
        location: '',
        deadline: '',
        ageRange: '',
        timeCommitment: '',
        majors: '',
        level: 'University',
        eligible: true,
        section: 'all',
        questions: []
    };

    const [formData, setFormData] = useState(initialState);
    const [questions, setQuestions] = useState([]);
    const [allForms, setAllForms] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [isFormDropdownOpen, setIsFormDropdownOpen] = useState(false);

    useEffect(() => {
        if (editData && isOpen) {
            setFormData({
                ...editData,
                badges: Array.isArray(editData.badges) ? editData.badges.join(', ') : editData.badges || '',
                majors: Array.isArray(editData.majors) ? editData.majors.join(', ') : editData.majors || '',
            });
            setQuestions(editData.questions || []);
            setSelectedFormId(editData.formId || '');
        } else if (!editData && isOpen) {
            setFormData(initialState);
            setQuestions([]);
            setSelectedFormId('');
        }

        if (isOpen && token) {
            fetchForms();
        }
    }, [editData, isOpen, token]);

    const fetchForms = async () => {
        try {
            const response = await fetch('/api/forms', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setAllForms(data.forms);
            }
        } catch (error) {
            console.error('Error fetching forms:', error);
        }
    };

    const handleFormSelection = (formId) => {
        setSelectedFormId(formId);
        const selectedForm = allForms.find(f => f.id === formId);
        if (selectedForm) {
            const mappedQuestions = (selectedForm.questions || []).map(q => {
                let type = 'text';
                if (q.type === 'long_text') type = 'textarea';
                if (q.type === 'multiple_choice' || q.type === 'dropdown') type = 'radio';
                if (q.type === 'checkbox') type = 'checkbox';
                
                return {
                    id: q.id || crypto.randomUUID(),
                    label: q.text || q.label || '', 
                    type: type,
                    required: !!q.required,
                    options: (q.options || []).map(opt => opt.label || opt)
                };
            });
            setQuestions(mappedQuestions);
            setFormData(prev => ({ 
                ...prev, 
                formId: selectedForm.id,
                formSlug: null 
            }));
        }
    };

    const [showAdvanced, setShowAdvanced] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [importLink, setImportLink] = useState('');
    const [isAutoFilling, setIsAutoFilling] = useState(false);
    const [fillMode, setFillMode] = useState('link'); // 'link' or 'pdf'
    const [tempFile, setTempFile] = useState(null);

    const typeColors = {
        'Internship': 'bg-primary/10 text-primary',
        'Volunteering': 'bg-secondary/15 text-secondary',
        'Bootcamp': 'bg-orange-500/10 text-orange-500',
        'Part-time': 'bg-blue-500/10 text-blue-500',
        'Full-time': 'bg-indigo-500/10 text-indigo-500',
        'Seminar': 'bg-purple-500/10 text-purple-500',
        'Job': 'bg-emerald-500/10 text-emerald-500',
        'Discounts': 'bg-rose-500/10 text-rose-500'
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAutoFill = async () => {
        if (fillMode === 'link' && !importLink) return;
        if (fillMode === 'pdf' && !tempFile) return;
        
        setIsAutoFilling(true);
        setError('');

        // Simulate AI extraction with a longer delay for the 'scanner' effect
        await new Promise(resolve => setTimeout(resolve, 2800));

        let mockData = {};

        if (fillMode === 'link') {
            const lowerLink = importLink.toLowerCase();
            if (lowerLink.includes('google')) {
                mockData = {
                    title: 'Associate Product Manager Intern 2026',
                    org: 'Google Azerbaijan',
                    location: 'Baku, Hybrid',
                    type: 'Internship',
                    excerpt: 'Shape the future of technology as an APM intern at Google.',
                    fullDescription: 'We are looking for creative and analytical minds to join our product teams for a 12-week intensive summer program. You will work on real products, collaborate with cross-functional teams, and learn from industry leaders.',
                    timeCommitment: '40h/week, 3 Months',
                    deadline: '2026-06-30'
                };
            } else if (lowerLink.includes('linkedin')) {
                mockData = {
                    title: 'LinkedIn Engineering Fellowship 2026',
                    org: 'LinkedIn Corp',
                    location: 'Remote / Global',
                    type: 'Internship',
                    excerpt: 'An exclusive fellowship for future software leaders.',
                    fullDescription: 'Join LinkedIn for a specialized engineering fellowship focused on distributed systems and networking. This program offers mentorship, networking, and hands-on experience in building large-scale infrastructure.',
                    timeCommitment: 'Full-time, 6 Months',
                    deadline: '2026-12-15'
                };
            } else {
                mockData = {
                    title: 'Extracted Opportunity from Web',
                    org: 'Web Recruitment Portal',
                    location: 'Remote',
                    type: 'Job',
                    excerpt: 'Auto-filled opportunity details from provided link.',
                    fullDescription: 'Details extracted from ' + importLink,
                    timeCommitment: 'Flexible',
                    deadline: '2026-04-01'
                };
            }
        } else {
            // PDF Extraction Mock
            mockData = {
                title: 'Data Science Resident (Deep Learning)',
                org: 'DeepMind Research',
                location: 'London / Remote',
                type: 'Internship',
                excerpt: 'Pushing the boundaries of AI through world-class research residency.',
                fullDescription: 'Extracted from ' + tempFile.name + ': This 6-month residency program is designed for PhD students or researchers looking to apply their knowledge to large-scale AI problems in a collaborative research environment.',
                timeCommitment: 'Flexible / Research-based',
                deadline: '2026-09-01',
                majors: 'Computer Science, Mathematics, Physics',
                level: 'Masters/PhD'
            };
        }

        setFormData(prev => ({ ...prev, ...mockData }));
        setIsAutoFilling(false);
        setImportLink('');
        setTempFile(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type === 'application/pdf') {
            setTempFile(file);
            setError('');
        } else {
            setError('Please upload a valid PDF file.');
        }
    };



    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const dataToSubmit = {
                ...formData,
                questions,
                typeColor: typeColors[formData.type] || 'bg-muted text-muted-foreground',
                badges: typeof formData.badges === 'string' ? formData.badges.split(',').map(b => b.trim()).filter(b => b) : formData.badges,
                majors: typeof formData.majors === 'string' ? formData.majors.split(',').map(m => m.trim()).filter(m => m) : formData.majors
            };

            await onAdd(dataToSubmit);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence mode="wait">
            {isOpen && (
                <div className="upload-panel-overlay opportunity-modal-overlay">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 40 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="upload-panel !bg-[#fafdfd] dark:!bg-[#041a1a] !border-[#062a2b]/10 dark:!border-white/10 !max-w-2xl"
                    >
                        {/* ── Header ── */}
                        <div className="panel-header !px-6 !py-4 border-b border-[#062a2b]/5 dark:border-white/5 bg-white/50 dark:bg-black/20 backdrop-blur-md sticky top-0 z-50">
                            <div>
                                <h1 className="text-[28px] font-display font-bold text-[#062a2b] dark:text-white flex items-center gap-2">
                                    <Layers className="w-6 h-6 text-primary" /> {editData ? 'Refine Opportunity' : 'Prepare Opportunity'}
                                </h1>
                                <p className="text-sm text-muted-foreground mt-1 font-medium">{editData ? 'Optimizing existing growth parameters' : 'Architecting new growth channels for the network'}</p>
                            </div>
                            <button className="close-btn !bg-muted/50 dark:!bg-white/5 hover:!bg-rose-500 hover:!text-white transition-all shadow-sm" onClick={onClose}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="overflow-y-auto max-h-[75vh] custom-scrollbar">
                            <form onSubmit={handleSubmit} className="p-6 space-y-6">
                                {error && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-4 rounded-2xl bg-rose-500/10 border border-rose-500/20 flex items-center gap-3 text-rose-500 text-[11px] font-black uppercase tracking-wider"
                                    >
                                        <AlertCircle size={16} /> {error}
                                    </motion.div>
                                )}

                                {/* ── AI Smart Import Section ── */}
                                {/* ── AI Smart Import Section ── */}
                                <section className="relative px-2">
                                    <div className="relative p-5 rounded-[1.5rem] bg-muted/20 dark:bg-white/5 border border-[#062a2b]/5 dark:border-white/5 overflow-hidden min-h-[140px] flex flex-col justify-center transition-all duration-500">
                                        
                                        {/* Scanner Effect */}
                                        <AnimatePresence>
                                            {isAutoFilling && (
                                                <motion.div 
                                                    initial={{ top: "-10%" }}
                                                    animate={{ top: "110%" }}
                                                    exit={{ opacity: 0 }}
                                                    transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                                                    className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent blur-[1px] z-20 pointer-events-none"
                                                />
                                            )}
                                        </AnimatePresence>

                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                                                    <Zap size={18} className={isAutoFilling ? "animate-pulse" : ""} strokeWidth={2.5} />
                                                </div>
                                                <div>
                                                    <h4 className="text-[11px] font-black uppercase tracking-[0.1em] text-[#062a2b] dark:text-white">Cognitive Auto-Fill</h4>
                                                    <p className="text-[9px] text-muted-foreground font-bold uppercase opacity-40">Form intelligence engine</p>
                                                </div>
                                            </div>

                                            <div className="flex bg-muted/20 dark:bg-black/20 p-1 rounded-lg border border-white/5">
                                                <button 
                                                    type="button" 
                                                    onClick={() => setFillMode('link')}
                                                    className={`px-3 py-1.5 rounded-md text-[8px] font-black uppercase tracking-wider transition-all ${fillMode === 'link' ? 'bg-white dark:bg-[#062a2b] shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                                                >
                                                    Link
                                                </button>
                                                <button 
                                                    type="button" 
                                                    onClick={() => setFillMode('pdf')}
                                                    className={`px-3 py-1.5 rounded-md text-[8px] font-black uppercase tracking-wider transition-all ${fillMode === 'pdf' ? 'bg-white dark:bg-[#062a2b] shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                                                >
                                                    PDF
                                                </button>
                                            </div>
                                        </div>

                                        <div className="relative">
                                            {fillMode === 'link' ? (
                                                <div className="flex gap-2">
                                                    <div className="relative flex-1">
                                                        <input
                                                            value={importLink}
                                                            onChange={(e) => setImportLink(e.target.value)}
                                                            className="w-full h-11 px-4 rounded-xl bg-muted/30 dark:bg-black/20 border border-transparent focus:border-primary/30 text-[11px] font-bold transition-all placeholder:opacity-30"
                                                            placeholder="Paste portal URL (Google, LinkedIn, etc.)"
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={handleAutoFill}
                                                        disabled={isAutoFilling || !importLink}
                                                        className={`
                                                            h-11 px-6 rounded-xl flex items-center gap-2 font-black text-[9px] uppercase tracking-[0.1em] transition-all
                                                            ${isAutoFilling
                                                                ? 'bg-muted text-muted-foreground'
                                                                : 'bg-primary text-white hover:bg-primary/90'}
                                                        `}
                                                    >
                                                        {isAutoFilling ? 'Scanning...' : 'Analyze'}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex gap-2 items-center">
                                                    <label className={`
                                                        flex-1 h-20 rounded-xl border border-dashed flex items-center justify-center gap-3 cursor-pointer transition-all
                                                        ${tempFile ? 'border-primary/50 bg-primary/5' : 'border-muted-foreground/30 hover:border-primary/40'}
                                                    `}>
                                                        <input type="file" accept=".pdf" className="hidden" onChange={handleFileChange} />
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${tempFile ? 'bg-primary text-white' : 'bg-muted-foreground/10 text-muted-foreground'}`}>
                                                            <Upload size={14} />
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">
                                                                {tempFile ? tempFile.name : 'Drop Opportunity PDF'}
                                                            </p>
                                                            <p className="text-[8px] font-bold text-muted-foreground/40 uppercase">Click to browse</p>
                                                        </div>
                                                    </label>
                                                    
                                                    <button
                                                        type="button"
                                                        onClick={handleAutoFill}
                                                        disabled={isAutoFilling || !tempFile}
                                                        className={`
                                                            h-20 px-6 rounded-xl flex flex-col items-center justify-center gap-1 font-black text-[9px] uppercase tracking-[0.1em] transition-all
                                                            ${isAutoFilling || !tempFile
                                                                ? 'bg-muted text-muted-foreground'
                                                                : 'bg-[#062a2b] dark:bg-white text-white dark:text-[#062a2b] shadow-lg shadow-[#062a2b]/10'}
                                                        `}
                                                    >
                                                        {isAutoFilling ? (
                                                            <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <>
                                                                <Sparkles size={16} />
                                                                <span>Scan</span>
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                {/* ── Step 1: Identity ── */}
                                <div className="space-y-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-[#062a2b] dark:bg-white text-white dark:text-[#062a2b] flex items-center justify-center text-xs font-bold shadow-lg">01</div>
                                        <div>
                                            <h3 className="text-sm font-bold text-[#062a2b] dark:text-white">Core Identity</h3>
                                            <p className="text-xs text-muted-foreground font-medium opacity-60">Basic definitions and positioning</p>
                                        </div>
                                        <div className="h-px flex-1 bg-border/40 ml-4" />
                                    </div>

                                    <div className="grid grid-cols-1 gap-6">
                                        <div className="space-y-2.5">
                                            <label className="text-xs font-bold text-foreground/80 dark:text-foreground ml-1 flex items-center gap-2 transition-colors">
                                                <div className="w-1.5 h-1.5 rounded-full bg-primary" /> Opportunity Title
                                            </label>
                                            <input 
                                                name="title" 
                                                value={formData.title} 
                                                onChange={handleChange} 
                                                required 
                                                className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 focus:bg-white dark:focus:bg-black/20 transition-all text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" 
                                                placeholder="e.g., Software Engineering Fellowship 2026" 
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div className="space-y-2.5">
                                                <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                    <Building size={14} /> Company / Organization
                                                </label>
                                                <input name="org" value={formData.org} onChange={handleChange} required className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 focus:bg-white transition-all text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="e.g., Google Azerbaijan" />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div className="space-y-2.5">
                                                <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                    <Settings2 size={14} /> Type of Program
                                                </label>
                                                <div className="relative">
                                                    <select name="type" value={formData.type} onChange={handleChange} className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold appearance-none cursor-pointer shadow-sm">
                                                        {Object.keys(typeColors).map(t => <option key={t} value={t}>{t}</option>)}
                                                    </select>
                                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                                        <MoreHorizontal size={16} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2.5">
                                                <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                    <MapPin size={14} /> Geographical Scope
                                                </label>
                                                <input name="location" value={formData.location} onChange={handleChange} required className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 transition-all text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="e.g., Baku, Hybrid" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Step 2: Context ── */}
                                <div className="space-y-8">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-[#062a2b] dark:bg-white text-white dark:text-[#062a2b] flex items-center justify-center text-xs font-bold shadow-lg">02</div>
                                        <div>
                                            <h3 className="text-sm font-bold text-[#062a2b] dark:text-white">Narrative & Logistics</h3>
                                            <p className="text-xs text-muted-foreground font-medium opacity-60">Experience detail and requirements</p>
                                        </div>
                                        <div className="h-px flex-1 bg-border/40 ml-4" />
                                    </div>

                                    <div className="grid grid-cols-1 gap-6">
                                        <div className="space-y-2.5">
                                            <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                <MessageSquare size={14} /> Viral Catchphrase / Excerpt
                                            </label>
                                            <input name="excerpt" value={formData.excerpt} onChange={handleChange} required className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 transition-all text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="Join the elite APM program and shape the future of Search..." />
                                        </div>

                                        <div className="space-y-2.5">
                                            <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                <FileText size={14} /> Comprehensive Description
                                            </label>
                                            <textarea name="fullDescription" value={formData.fullDescription} onChange={handleChange} required className="w-full p-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold resize-none min-h-[140px] shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="Outline responsibilities, curriculum, tech stack, and benefits..." />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            <div className="space-y-2.5">
                                                <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                    <GraduationCap size={14} /> Target Education
                                                </label>
                                                <div className="relative">
                                                    <select name="level" value={formData.level} onChange={handleChange} className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold appearance-none cursor-pointer shadow-sm">
                                                        <option value="University">University Students</option>
                                                        <option value="High School">High Schoolers</option>
                                                        <option value="Masters/PhD">Postgraduates (MA/PhD)</option>
                                                        <option value="General">Open to All Categories</option>
                                                    </select>
                                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-40">
                                                        <MoreHorizontal size={16} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="space-y-2.5">
                                                <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                    <Clock size={14} /> Intensity / Commitment
                                                </label>
                                                <input name="timeCommitment" value={formData.timeCommitment} onChange={handleChange} required className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 transition-all text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="e.g., 20h/week, Summer Term" />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Advanced Details Toggle ── */}
                                <div className="pt-4 flex justify-center">
                                    <div className="advanced-toggle-pill" onClick={() => setShowAdvanced(!showAdvanced)}>
                                        <span>Detailed Data</span>
                                        <div className={`toggle-switch-pill ${showAdvanced ? 'active' : ''}`}></div>
                                    </div>
                                </div>

                                    <AnimatePresence>
                                        {showAdvanced && (
                                            <motion.div 
                                                initial={{ height: 0, opacity: 0, scale: 0.98 }} 
                                                animate={{ height: 'auto', opacity: 1, scale: 1 }} 
                                                exit={{ height: 0, opacity: 0, scale: 0.98 }} 
                                                className="relative"
                                            >
                                                <div className="p-6 mt-4 rounded-[1.5rem] bg-muted/10 dark:bg-black/10 border border-border/30 space-y-8">
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                                        <div className="space-y-2.5">
                                                            <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                                <Calendar size={14} /> Application Deadline
                                                            </label>
                                                            <div className="relative">
                                                                <input
                                                                    type="date"
                                                                    name="deadline"
                                                                    value={formData.deadline}
                                                                    onChange={handleChange}
                                                                    min={new Date().toISOString().split('T')[0]}
                                                                    className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold appearance-none cursor-pointer shadow-sm"
                                                                />
                                                                <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 opacity-30 pointer-events-none" />
                                                            </div>
                                                        </div>
                                                        <div className="space-y-2.5">
                                                            <label className="text-xs font-bold text-muted-foreground ml-1 h-5 flex items-center gap-1.5">
                                                                <User size={14} /> Age Limit / Range
                                                            </label>
                                                            <input name="ageRange" value={formData.ageRange} onChange={handleChange} className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="e.g., 18-24 Preferred" />
                                                        </div>
                                                    </div>
                                                    <div className="space-y-2.5">
                                                        <label className="text-xs font-bold text-primary ml-1 flex items-center gap-2">
                                                            <ListChecks size={14} /> Target Majors <span className="text-[10px] opacity-40 lowercase font-medium">(comma separated)</span>
                                                        </label>
                                                        <input name="majors" value={formData.majors} onChange={handleChange} className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="CS, Software Engineering, Applied Math" />
                                                    </div>
                                                    <div className="space-y-2.5">
                                                        <label className="text-xs font-bold text-muted-foreground ml-1 flex items-center gap-2">
                                                            <Briefcase size={14} /> Quality Badges <span className="text-[10px] opacity-40 lowercase font-medium">(comma separated)</span>
                                                        </label>
                                                        <input name="badges" value={formData.badges} onChange={handleChange} className="w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border border-border/10 focus:border-primary/40 text-sm font-bold shadow-sm placeholder:font-medium placeholder:opacity-30" placeholder="Paid, Certified, Remote-First" />
                                                    </div>
                                                    <div className="flex items-center gap-4 p-5 rounded-2xl bg-white/40 dark:bg-black/20 border border-white/50 dark:border-white/5 shadow-sm">
                                                        <input type="checkbox" name="eligible" checked={formData.eligible} onChange={handleChange} className="w-5 h-5 rounded-lg border-border text-primary focus:ring-primary shadow-inner cursor-pointer" />
                                                        <div>
                                                            <p className="text-xs font-bold text-foreground opacity-90">Public Matching Eligibility</p>
                                                            <p className="text-[10px] text-muted-foreground font-medium opacity-60">Visible to users whose preferences intersect with these attributes</p>
                                                        </div>
                                                    </div>

                                                    {/* ── Section 03: Application Questionnaire ── */}
                                                    <div className="space-y-8 pt-4 pb-4">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-9 h-9 rounded-xl bg-[#062a2b] dark:bg-white text-white dark:text-[#062a2b] flex items-center justify-center text-xs font-bold shadow-lg">03</div>
                                                                <div>
                                                                    <h3 className="text-sm font-bold text-[#062a2b] dark:text-white">Application Questionnaire</h3>
                                                                    <p className="text-xs text-muted-foreground font-medium opacity-60">Choose a form for your applicants</p>
                                                                </div>
                                                            </div>
                                                            <button 
                                                                type="button" 
                                                                onClick={() => window.open(`/${localStorage.getItem('lang') || 'en'}/forms/create`, '_blank')}
                                                                className="h-8 px-4 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-[9px] font-black uppercase tracking-wider transition-all flex items-center gap-2"
                                                            >
                                                                <Plus size={12} /> Create New
                                                            </button>
                                                        </div>

                                                        <div className="space-y-4">
                                                            <div className="relative">
                                                                <div 
                                                                    className={`w-full h-11 px-5 rounded-xl bg-muted/10 dark:bg-white/5 border ${isFormDropdownOpen ? 'border-primary/40' : 'border-border/10'} flex items-center justify-between cursor-pointer transition-all shadow-sm hover:bg-muted/20 dark:hover:bg-white/10`}
                                                                    onClick={() => setIsFormDropdownOpen(!isFormDropdownOpen)}
                                                                >
                                                                    <span className={`text-sm font-bold ${!selectedFormId ? 'opacity-30' : ''}`}>
                                                                        {selectedFormId ? allForms.find(f => f.id === selectedFormId)?.title : 'Select a previously created form...'}
                                                                    </span>
                                                                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${isFormDropdownOpen ? 'rotate-90 text-primary' : 'opacity-40'}`} />
                                                                </div>

                                                                <AnimatePresence>
                                                                    {isFormDropdownOpen && (
                                                                        <motion.div 
                                                                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                                            animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                                            transition={{ duration: 0.2 }}
                                                                            className="absolute left-0 right-0 top-full mt-2 bg-white dark:bg-[#062424] border border-border/10 dark:border-white/10 rounded-2xl shadow-2xl z-[999] max-h-60 overflow-y-auto custom-scrollbar backdrop-blur-2xl"
                                                                        >
                                                                            <div className="p-2 space-y-1">
                                                                                {allForms.length === 0 ? (
                                                                                    <div className="p-4 text-center text-xs font-bold text-muted-foreground opacity-50 uppercase tracking-widest">
                                                                                        No forms found
                                                                                    </div>
                                                                                ) : (
                                                                                    allForms.map(f => (
                                                                                        <div 
                                                                                            key={f.id}
                                                                                            onClick={() => {
                                                                                                handleFormSelection(f.id);
                                                                                                setIsFormDropdownOpen(false);
                                                                                            }}
                                                                                            className={`px-4 py-3 rounded-xl text-sm font-bold cursor-pointer transition-all flex items-center justify-between group ${selectedFormId === f.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50 dark:hover:bg-white/5 text-foreground'}`}
                                                                                        >
                                                                                            <span>{f.title}</span>
                                                                                            {selectedFormId === f.id && <CheckCircle2 size={14} className="animate-in zoom-in" />}
                                                                                        </div>
                                                                                    ))
                                                                                )}
                                                                            </div>
                                                                        </motion.div>
                                                                    )}
                                                                </AnimatePresence>
                                                            </div>
                                                            {selectedFormId && (
                                                                <div className="p-4 rounded-xl bg-primary/5 border border-primary/10 flex items-center gap-3">
                                                                    <CheckCircle2 size={16} className="text-primary" />
                                                                    <p className="text-[10px] font-bold text-primary uppercase tracking-wider">
                                                                        {allForms.find(f => f.id === selectedFormId)?.questions?.length || 0} questions will be imported
                                                                    </p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </form>
                            </div>

                        {/* ── Footer ── */}
                        <div className="p-8 border-t border-[#062a2b]/5 dark:border-white/5 bg-white/80 dark:bg-[#041a1a]/80 backdrop-blur-md flex items-center justify-between gap-4 sticky bottom-0 z-50">
                            <button type="button" className="group px-4 h-11 rounded-xl text-[10px] font-black uppercase tracking-widest text-muted-foreground/60 hover:text-rose-500 transition-all border-none cursor-pointer flex items-center gap-2" onClick={onClose}>
                                <X size={14} /> Discard
                            </button>
                            <button
                                type="submit"
                                className={`
                                    h-12 px-8 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all
                                    ${loading || !formData.title 
                                        ? 'bg-muted text-muted-foreground cursor-not-allowed opacity-50' 
                                        : 'bg-[#062a2b] dark:bg-white text-white dark:text-[#062a2b] hover:opacity-90 active:scale-[0.98]'}
                                `}
                                onClick={handleSubmit}
                                disabled={loading || !formData.title}
                            >
                                {loading ? 'Processing...' : (editData ? 'Update Matrix' : 'Deploy Opportunity')}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default AddOpportunityModal;
